/**
 * i18n Helper Utilities
 * 
 * This module provides helper functions for accessing i18n translations
 * in utility files where direct i18n import would cause circular dependencies.
 */

/**
 * Gets i18n translation at runtime to avoid circular dependency issues.
 * This function safely accesses the i18n instance from the global scope,
 * making it usable in utility files that can't directly import i18n.
 * 
 * @param {string} key - The translation key (e.g., 'svgIcon.plusCircleIcon')
 * @param {string} fallback - Fallback text if translation is not available
 * @returns {string} - Translated text or fallback
 * 
 * @example
 * const label = getI18nTranslation('svgIcon.plusCircleIcon', 'Plus Circle Icon');
 */
export function getI18nTranslation(key, fallback) {
  try {
    // Prefer globalThis for broader environment support (works in browsers, workers, Node when polyfilled)
    const g = typeof globalThis !== 'undefined' ? globalThis : (typeof window !== 'undefined' ? window : undefined);
    
    // Access Vue's global properties if available
    if (g && g.__VUE_I18N__) {
      return g.__VUE_I18N__.global.t(key);
    }
    
    // Try to get from any mounted Vue instance
    if (g && g.__vue_app__) {
      const i18nInstance = g.__vue_app__.config.globalProperties.$i18n;
      if (i18nInstance) {
        return i18nInstance.global.t(key);
      }
    }
  } catch (e) {
    // Silently fall back to prevent errors in edge cases
  }
  return fallback;
}
